
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { FAQ, FAQInsert, FAQUpdate } from "./useFAQs";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";

const faqSchema = z.object({
  question: z.string().min(5, "A pergunta deve ter pelo menos 5 caracteres"),
  answer: z.string().min(10, "A resposta deve ter pelo menos 10 caracteres"),
  category: z.string().min(2, "A categoria é obrigatória")
});

interface FAQFormProps {
  initial?: FAQ;
  onSave: (data: FAQInsert | FAQUpdate) => Promise<void>;
  onCancel: () => void;
  isProcessing?: boolean;
}

const FAQForm: React.FC<FAQFormProps> = ({
  initial,
  onSave,
  onCancel,
  isProcessing = false
}) => {
  const form = useForm<z.infer<typeof faqSchema>>({
    resolver: zodResolver(faqSchema),
    defaultValues: {
      question: initial?.question || "",
      answer: initial?.answer || "",
      category: initial?.category || ""
    }
  });

  const handleSubmit = async (values: z.infer<typeof faqSchema>) => {
    try {
      await onSave(values);
      form.reset();
    } catch (error) {
      console.error("Error saving FAQ:", error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Categoria</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Ex: Produtos, Serviços, Suporte"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="question"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Pergunta</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Digite a pergunta frequente"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="answer"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Resposta</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Digite a resposta para a pergunta"
                  className="min-h-[120px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter className="mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancelar
          </Button>
          <Button 
            type="submit"
            className="bg-toctoc-orange hover:bg-orange-600"
            disabled={isProcessing}
          >
            {isProcessing ? "Salvando..." : initial ? "Atualizar" : "Criar"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

export default FAQForm;
